import React, { useEffect } from "react";
import {
  SACard,
  SAButton,
  SAModal,
  SAText,
  SAUXTheme
} from '@saux/design-system-react';
import styled from 'styled-components';

const mobileWidth = 600;

type CrossHierarchyWarningProps = {
  onClickClose: Function;
};

type StyleProps = {
  error?: boolean;
  theme?: SAUXTheme;
};

const Modal = styled(SAModal)`
  display: flex;
  top: 50%;
  left: 50%;
  justify-content: center;
  align-items: center;
  overflow: auto;
`;

const CrossHierarchy = styled(SACard)`
  ${({ theme }: StyleProps) => {
    return `
      display: flex;
      flex-direction: column;
      width: 600px;
      overflow: auto;
      margin: auto;

      section {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
      }

      @media only screen and (max-width: ${mobileWidth}px) {
        flex-grow: 1;
        width: auto;
        width: 100%;
        height: 100%;
        border-radius: 0;

        section {
          padding: ${theme?.size.spacer.large} ${theme?.size.spacer.medium};
        }
      }
    `;
  }};
`;

const ModalHeader = styled.div`
  padding-bottom: 45px;
`;

const ModalTitle = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  height: 30px;

  button:last-child {
    display: none;

    &:focus {
      outline: auto;
    } 
  }

  @media only screen and (max-width: ${mobileWidth}px) {
    button:last-child {
      display: flex;
    }
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const ModalFooter: any = styled.div`
  ${({ theme }: StyleProps) => {
    return `
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      align-items: flex-end;
      padding-top: 60px;

      button {
        font-weight: ${theme?.font.primary.weight.normal};
        margin-left: ${theme?.size.spacer.medium};

        &:first-child {
          margin-left: 0;
        }

        &:last-child {
          font-weight: ${theme?.font.primary.weight.bold};
        }
      }

      @media only screen and (max-width: ${mobileWidth}px) {
        justify-content: center;
        height: 100%;
      }
    `;
  }};
`;

const Note = styled.div`
  font-size: 20px;
  line-height: 26px;
`;

export default function ModalCrossHierarchyWarning({ onClickClose }: CrossHierarchyWarningProps) {
  useEffect(() => {
    function focusDefault() {
      const modal = document.querySelector('#modalcrosshierarchywarning') as HTMLElement;
      if (modal) {
        const element = modal.querySelector('button.sa-button') as HTMLElement;
        if (element) {
          element.focus();
        }
      } 
    }

    function focusModalCrossHierarchyWarning(e: any) {
      const modal = document.querySelector('#modalcrosshierarchywarning') as HTMLElement;
      const root = document.querySelector('#root') as HTMLElement;
      if (modal && root) {
        if ((e.type === 'focusout' && root.contains(e.relatedTarget)) || (e.type === 'focusin' && root.contains(e.target))) {
          let element = null;
          element = modal.querySelector('button') as HTMLElement;
          if (element) {  
            element.focus();
          }
        }
      }
    }

    focusDefault();
    document.addEventListener('focusin', focusModalCrossHierarchyWarning);
    document.addEventListener('focusout', focusModalCrossHierarchyWarning);

    return () => {
      document.removeEventListener('focusin', focusModalCrossHierarchyWarning);
      document.removeEventListener('focusout', focusModalCrossHierarchyWarning);
    }
  }, []);
  
  function handleClickCrossHierarchyWarningModal(event: React.MouseEvent) {
    event.stopPropagation();
  }

  function handleClickClose(event: React.MouseEvent) {
    event.stopPropagation();
    onClickClose(event);
  }

  return (
    <Modal id="modalcrosshierarchywarning" onClickHandler={handleClickCrossHierarchyWarningModal}>
      <CrossHierarchy variant="minimal">
        <ModalHeader>
          <ModalTitle>
            <SAText type="heading-2" text="Unable to Process Request" />
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          <Note>We are unable to process your request at this time. Please contact <a href="mailto:SalesOperations@stateauto.com">SalesOperations@stateauto.com</a> for assistance.</Note>
        </ModalBody>
        <ModalFooter>
          <SAButton label="Close" onClick={handleClickClose} variant="large" color="secondary" textTransform="uppercase" />
        </ModalFooter>
      </CrossHierarchy>
    </Modal>
  );
}
